import React from 'react';
import { useTranslation } from 'react-i18next';
import HyperliquidNav from '../../components/trading/HyperliquidNav';
import '../../components/trading/hyperliquid-theme.css';
import '../../components/trading/TradingComponents.css';
import { useStakingSkeleton } from './useStakingSkeleton';
import StakesPanel from './StakesPanel';
import UnStakesPanel from './UnStakesPanel';
import StakeAmountModal from './modals/StakeAmountModal';
import StakeDetailsModal from './modals/StakeDetailsModal';
import UnstakeRequestModal from './modals/UnstakeRequestModal';
import UnstakeClaimModal from './modals/UnstakeClaimModal';
import StakingLoadingOverlay from './StakingLoadingOverlay';
import StakingResultModal from './StakingResultModal';
import './StakingPage.css';

const StakingPage = () => {
  const { t } = useTranslation();
  const {
    activeTab,
    setActiveTab,
    overlay,
    portfolio,
    activeStakes,
    unstakeSummary,
    unstakeItems,
    dashboardLoading,
    dashboardError,
    reloadDashboard,
    resultModal,
    closeResultModal,
    modals,
    actions,
  } = useStakingSkeleton();

  return (
    <div className="staking-scope">
      <HyperliquidNav />
      <main className="staking-page">
        <div className="staking-page-inner">
          <h1 className="staking-page-title">{t('topbar.staking', 'Staking')}</h1>

          {dashboardLoading ? (
            <div className="staking-dashboard-loading" role="status">
              <div className="staking-loading-dots staking-loading-dots--inline" aria-hidden>
                {Array.from({ length: 9 }).map((_, i) => (
                  <span key={i} className="staking-loading-dot" />
                ))}
              </div>
              <p>{t('common.loading', 'Loading...')}</p>
            </div>
          ) : null}

          {dashboardError ? (
            <div className="staking-dashboard-error" role="alert">
              <p>{t('staking.load_error', 'Could not load staking data.')}</p>
              <button type="button" className="staking-btn staking-btn-secondary" onClick={reloadDashboard}>
                {t('common.refresh', 'Refresh')}
              </button>
            </div>
          ) : null}

          {!dashboardLoading && !dashboardError && portfolio ? (
            <>
              <div className="staking-tabs" role="tablist" aria-label={t('topbar.staking', 'Staking')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'stakes'}
                  className={`staking-tab ${activeTab === 'stakes' ? 'staking-tab--active' : ''}`}
                  onClick={() => setActiveTab('stakes')}
                >
                  {t('staking.tab_stakes', 'Stakes')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'unstakes'}
                  className={`staking-tab ${activeTab === 'unstakes' ? 'staking-tab--active' : ''}`}
                  onClick={() => setActiveTab('unstakes')}
                >
                  {t('staking.tab_unstakes', 'Un-Stakes')}
                </button>
              </div>

              <div className="staking-tab-panels">
                {activeTab === 'stakes' ? (
                  <div role="tabpanel">
                    <StakesPanel
                      portfolio={portfolio}
                      activeStakes={activeStakes}
                      onStake={modals.openStake}
                      onMoreDetails={modals.openStakeDetails}
                      onUnstakeCard={(row) => modals.openUnstakeRequest(row)}
                    />
                  </div>
                ) : (
                  <div role="tabpanel">
                    <UnStakesPanel
                      summary={unstakeSummary}
                      items={unstakeItems}
                      tokenSymbol={portfolio.stakeTokenSymbol}
                      onClaimAll={actions.submitClaimAll}
                      onCancelRequest={actions.cancelUnstakeRequest}
                      onClaimAvailable={(row) => modals.openUnstakeClaim(row)}
                    />
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </main>

      {portfolio && modals.stakeOpen ? (
        <StakeAmountModal
          portfolio={portfolio}
          onClose={modals.closeStake}
          onSubmit={(amount) => actions.submitStake(amount)}
        />
      ) : null}

      <StakeDetailsModal detail={modals.stakeDetails} onClose={modals.closeStakeDetails} />

      <UnstakeRequestModal
        data={modals.unstakeRequest}
        onClose={modals.closeUnstakeRequest}
        onSubmit={actions.submitUnstakeRequest}
      />

      <UnstakeClaimModal
        data={modals.unstakeClaim}
        onClose={modals.closeUnstakeClaim}
        onSubmit={actions.submitClaim}
      />

      {overlay ? <StakingLoadingOverlay variant={overlay} /> : null}

      <StakingResultModal result={resultModal} onClose={closeResultModal} />
    </div>
  );
};

export default StakingPage;
